var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { clone, Handler } from "./Handler";
import { AwaitQueue } from "./AwaitQueue";
import { Producer } from "./Producer";
import { Consumer } from "./Consumer";
import { Delegate1, MulticastDelegate1 } from "flavr-common";
import { serverConnection } from "../server/ServerConnectionService";
const WAITING_FOR_ANSWER_TIMEOUT = 2 * 60 * 1000;
class ConsumerCreationTask {
    constructor(consumerOptions) {
        this.consumerOptions = consumerOptions;
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
    }
}
export class Transport {
    constructor(remoteClientId, { direction, id, iceParameters, iceCandidates, dtlsParameters, sctpParameters, iceServers, iceTransportPolicy, additionalSettings, proprietaryConstraints, extendedRtpCapabilities, canProduceByKind }) {
        this.remoteClientId = remoteClientId;
        this._closed = false;
        this.producers = new Map();
        this.consumers = new Map();
        this._connectionState = "new";
        this._pendingConsumerTasks = [];
        this._consumerCreationInProgress = false;
        this._pendingPauseConsumers = new Map();
        this._consumerPauseInProgress = false;
        this._pendingResumeConsumers = new Map();
        this._consumerResumeInProgress = false;
        this.onNewconsumerDelegate = new MulticastDelegate1();
        this.onConnectionstatechangeDelegate = new Delegate1();
        this.onAnswerDelegate = new MulticastDelegate1();
        this._id = id;
        this._direction = direction;
        this._extendedRtpCapabilities = extendedRtpCapabilities;
        this._canProduceByKind = canProduceByKind;
        /* this._maxSctpMessageSize =
          sctpParameters ? sctpParameters.maxMessageSize : null; */
        this.awaitQueue = new AwaitQueue(`Transport${this._direction}`, {
            ClosedErrorClass: Error
        });
        additionalSettings = clone(additionalSettings, {});
        delete additionalSettings.iceServers;
        delete additionalSettings.iceTransportPolicy;
        delete additionalSettings.bundlePolicy;
        delete additionalSettings.rtcpMuxPolicy;
        delete additionalSettings.sdpSemantics;
        this.handler = new Handler();
        this.handler.init({
            direction,
            iceParameters,
            iceCandidates,
            dtlsParameters,
            sctpParameters,
            iceServers,
            iceTransportPolicy,
            additionalSettings,
            proprietaryConstraints,
            extendedRtpCapabilities
        });
        this.handleHandler();
    }
    get id() {
        return this._id;
    }
    get closed() {
        return this._closed;
    }
    get direction() {
        return this._direction;
    }
    get connectionState() {
        return this.handler.connectionState;
    }
    close() {
        if (this._closed)
            return;
        this._closed = true;
        this.clear();
        this.clearWaitingForAnswer();
        this.awaitQueue.close();
        this.handler.close();
        for (const producer of this.producers.values()) {
            producer.transportClosed();
        }
        this.producers.clear();
        for (const consumer of this.consumers.values()) {
            consumer.transportClosed();
        }
        this.consumers.clear();
    }
    clear() {
        this.onNewconsumerDelegate.clear();
        this.onConnectionstatechangeDelegate.clear();
        this.onAnswerDelegate.clear();
    }
    restartIce(_a) {
        return __awaiter(this, arguments, void 0, function* ({ iceParameters }) {
            if (this._closed)
                throw new Error("closed");
            // Enqueue command.
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                /* const offer = */ yield this.handler.restartIce(iceParameters);
                /* if (offer && offer.sdp) {
                  serverConnection.send({
                    channel: "rtc:offer",
                    payload: {
                      id: this.id,
                      sdp: offer.sdp,
                      target: this.remoteClientId
                    }
                  });
        
                  await this.waitForAnswer().catch(async error => {
                    console.error("[multiplayer]", error);
        
                    await this.handler.rollbackLocalDescription();
                  });
        
                  console.log("[multiplayer] restartIce finished", this.id);
                } */
            }), "transport.restartIce()")
                .catch((error) => {
                console.error(error);
            });
        });
    }
    updateIceServers() {
        return __awaiter(this, arguments, void 0, function* ({ iceServers } = {}) {
            if (this._closed)
                throw new Error("closed");
            else if (!Array.isArray(iceServers))
                throw new TypeError("missing iceServers");
            // Enqueue command.
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.updateIceServers(iceServers);
            }), "transport.updateIceServers()")
                .catch((error) => {
                console.error(error);
            });
        });
    }
    setRtpEncodingParametersByKind(kind, params) {
        this.awaitQueue
            .push(() => __awaiter(this, void 0, void 0, function* () {
            yield this.handler.setRtpEncodingParametersByKind(kind, params);
        }))
            .catch((error) => {
            console.error(error);
        });
    }
    updateBandwidthRestriction(bitrate, priority, // RTCPriorityType,
    networkPriority, // RTCPriorityType,
    kind = undefined) {
        this.awaitQueue
            .push(() => __awaiter(this, void 0, void 0, function* () {
            yield this.handler.updateBandwidthRestriction(bitrate, priority, networkPriority, kind);
        }))
            .catch((error) => {
            console.error(error);
        });
    }
    produce(producerId_1) {
        return __awaiter(this, arguments, void 0, function* (producerId, { stream, track, encodings, codecOptions, codec, codecs, enabledFunc, stopTracks = true, disableTrackOnPause = true, zeroRtpOnPause = false } = {}) {
            if (!stream)
                throw new TypeError("missing stream");
            if (!track)
                throw new TypeError("missing track");
            else if (this._direction !== "send")
                throw new Error("not a sending Transport");
            else if (!this._canProduceByKind[track.kind])
                throw new Error(`cannot produce ${track.kind}`);
            else if (track.readyState === "ended") {
                throw new Error("track ended");
            }
            return yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                let normalizedEncodings;
                if (encodings) {
                    normalizedEncodings = [...encodings];
                }
                const { localId, rtpParameters, rtpSender, transceiver, originOffer } = yield this.handler.send(stream, track, normalizedEncodings || [], codecOptions, codec, codecs, enabledFunc);
                try {
                    if (!this.onProduceDelegate) {
                        throw new Error("onProduceDelegate undefined");
                    }
                    console.warn("wait onProduceDelegate", this.remoteClientId);
                    yield this.onProduceDelegate(track.kind, rtpParameters);
                    console.warn("finished onProduceDelegate", this.remoteClientId);
                    for (let i = 0; i < 5; i++) {
                        console.warn("send newConsumer", this.remoteClientId);
                        serverConnection.send({
                            channel: "rtc:newConsumer",
                            payload: {
                                id: this.id,
                                sdp: originOffer.sdp || "",
                                target: this.remoteClientId,
                                mediaId: localId,
                                producerId,
                                kind: track.kind
                            }
                        });
                        try {
                            console.warn("producer: waitForAnswer", this.remoteClientId);
                            yield this.waitForAnswer();
                            console.warn("producer: waitForAnswer finished", this.remoteClientId);
                            break;
                        }
                        catch (error) {
                            console.error(error);
                            if (i >= 4) {
                                yield this.handler.rollbackLocalDescription();
                                throw error;
                            }
                        }
                    }
                    const producer = new Producer({
                        id: producerId,
                        localId,
                        rtpSender,
                        transceiver,
                        stream,
                        track,
                        rtpParameters,
                        stopTracks,
                        disableTrackOnPause,
                        zeroRtpOnPause
                    });
                    this.producers.set(producer.id, producer);
                    this.handleProducer(producer);
                    return producer;
                }
                catch (error) {
                    this.handler.stopSending(localId).catch(() => { });
                    throw error;
                }
            }), "transport.produce()")
                .catch((error) => {
                if (stopTracks) {
                    try {
                        track.stop();
                    }
                    catch (error2) { }
                }
                throw error;
            });
        });
    }
    consume(_a) {
        return __awaiter(this, arguments, void 0, function* ({ id, producerId, kind, rtpParameters, sdp }) {
            rtpParameters = clone(rtpParameters, undefined);
            if (this._closed)
                throw new Error("closed");
            else if (this._direction !== "recv")
                throw new Error("not a receiving Transport");
            else if (typeof id !== "string")
                throw new TypeError("missing id");
            else if (typeof producerId !== "string")
                throw new TypeError("missing producerId");
            else if (kind !== "audio" && kind !== "video")
                throw new TypeError(`invalid kind '${kind}'`);
            const consumerCreationTask = new ConsumerCreationTask({
                id,
                producerId,
                kind,
                rtpParameters,
                sdp
            });
            this._pendingConsumerTasks.push(consumerCreationTask);
            if (!this._consumerCreationInProgress) {
                this.createPendingConsumers();
            }
            return yield consumerCreationTask.promise;
        });
    }
    handleOffer(sdp, mediaId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("handle offer", this._id, this.handler.peerConnection);
            let result;
            try {
                result = yield this.handler.handleOffer(sdp, mediaId);
            }
            finally {
                const sdp = result ? result.answer.sdp : "";
                serverConnection.send({
                    channel: "rtc:answer",
                    payload: {
                        id: this.id,
                        sdp: sdp,
                        target: this.remoteClientId
                    }
                });
            }
        });
    }
    handleAnswer(sdp) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (sdp) {
                    yield this.handler.handleAnswer(sdp);
                }
                else {
                    console.warn("handleAnswer: Got error answer");
                }
            }
            finally {
                this.onAnswerDelegate.invoke(sdp);
            }
        });
    }
    handleIce(candidate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!candidate) {
                return;
            }
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.handleIce(candidate);
            }), "transport.handleIce()")
                .catch((error) => {
                console.error(error);
            });
        });
    }
    getStats() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._closed)
                return yield Promise.resolve({ forEach: () => { } });
            return yield this.handler.getStats();
        });
    }
    checkIsUsingTurn() {
        if (this._closed)
            return false;
        return this.handler.checkIsUsingTurn();
    }
    createPendingConsumers() {
        return __awaiter(this, void 0, void 0, function* () {
            this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                this._consumerCreationInProgress = true;
                const pendingConsumerTasks = [...this._pendingConsumerTasks];
                // Clear pending Consumer tasks.
                this._pendingConsumerTasks = [];
                for (const task of pendingConsumerTasks) {
                    const { id, kind, rtpParameters, sdp } = task.consumerOptions;
                    try {
                        const result = yield this.handler.receive([
                            {
                                trackId: id,
                                kind: kind,
                                rtpParameters,
                                sdp
                            }
                        ]);
                        const results = result.results;
                        serverConnection.send({
                            channel: "rtc:answer",
                            payload: {
                                id: this.id,
                                sdp: result.originAnswer.sdp || "",
                                target: this.remoteClientId
                            }
                        });
                        for (let idx = 0; idx < results.length; idx++) {
                            const task = pendingConsumerTasks[idx];
                            const result = results[idx];
                            const { id, producerId, rtpParameters } = task.consumerOptions;
                            const { localId, rtpReceiver, transceiver, stream, track } = result;
                            const consumer = new Consumer({
                                id: id,
                                localId,
                                producerId: producerId,
                                rtpReceiver,
                                transceiver,
                                stream,
                                track,
                                rtpParameters
                            });
                            this.consumers.set(consumer.id, consumer);
                            this.handleConsumer(consumer);
                            this.onNewconsumerDelegate.invoke(consumer);
                            task.resolve(consumer);
                        }
                    }
                    catch (error) {
                        console.error(error);
                        serverConnection.send({
                            channel: "rtc:answer",
                            payload: {
                                id: this.id,
                                sdp: "",
                                target: this.remoteClientId
                            }
                        });
                        break;
                    }
                }
                this._consumerCreationInProgress = false;
            }), "transport._createPendingConsumers()")
                .then(() => {
                // There are pending Consumer tasks, enqueue their creation.
                if (this._pendingConsumerTasks.length > 0) {
                    this.createPendingConsumers();
                }
            })
                // NOTE: We only get here when the await queue is closed.
                .catch(() => { });
        });
    }
    pausePendingConsumers() {
        this.awaitQueue
            .push(() => __awaiter(this, void 0, void 0, function* () {
            this._consumerPauseInProgress = true;
            const pendingPauseConsumers = Array.from(this._pendingPauseConsumers.values());
            // Clear pending pause Consumer map.
            this._pendingPauseConsumers.clear();
            yield this.handler.pauseReceiving(pendingPauseConsumers.map((consumer) => consumer.localId));
        }), "consumer @pause event")
            .catch(() => { })
            .finally(() => {
            this._consumerPauseInProgress = false;
            // There are pending Consumers to be paused, do it.
            if (this._pendingPauseConsumers.size > 0) {
                this.pausePendingConsumers();
            }
        });
    }
    resumePendingConsumers() {
        this.awaitQueue
            .push(() => __awaiter(this, void 0, void 0, function* () {
            this._consumerResumeInProgress = true;
            const pendingResumeConsumers = Array.from(this._pendingResumeConsumers.values());
            // Clear pending resume Consumer map.
            this._pendingResumeConsumers.clear();
            yield this.handler.resumeReceiving(pendingResumeConsumers.map((consumer) => consumer.localId));
        }), "consumer @resume event")
            .catch(() => { })
            .finally(() => {
            this._consumerResumeInProgress = false;
            // There are pending Consumer to be resumed, do it.
            if (this._pendingResumeConsumers.size > 0) {
                this.resumePendingConsumers();
            }
        });
    }
    handleHandler() {
        ;
        this.handler.onConnectDelegate.add((dtlsParameters, callback, errback) => {
            if (this._closed) {
                errback(new Error("closed"));
                return;
            }
            if (this.onConnectDelegate) {
                this.onConnectDelegate(dtlsParameters, callback, errback);
            }
            else {
                callback();
            }
        });
        this.handler.onConnectionstatechangeDelegate.add((connectionState) => {
            if (connectionState === this._connectionState)
                return;
            console.warn("connection state changed to %s", connectionState);
            this._connectionState = connectionState;
            if (!this._closed) {
                this.onConnectionstatechangeDelegate.invoke(connectionState);
            }
        });
        this.handler.onIcecandidateDelegate.set((candidate) => {
            serverConnection.send({
                channel: "rtc:ice",
                payload: {
                    id: this.id,
                    candidate,
                    target: this.remoteClientId
                }
            });
        });
        this.handler.onNegotiationNeededDelegate.set(() => __awaiter(this, void 0, void 0, function* () {
            yield this.createOffer();
        }));
        this.handler.onTrackDelegate.set((result) => {
            if (!result.transceiver.mid)
                return;
            let foundConsumer;
            for (const consumer of this.consumers.values()) {
                if (consumer.localId === result.transceiver.mid) {
                    foundConsumer = consumer;
                    break;
                }
            }
            if (foundConsumer) {
                if (foundConsumer.stream !== result.stream) {
                    foundConsumer.changeStream(result.stream);
                }
            }
        });
    }
    handleProducer(producer) {
        producer.onCloseDelegate.add(() => {
            this.producers.delete(producer.id);
            if (this._closed)
                return;
            this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.stopSending(producer.localId);
            }), "producer @close event")
                .catch((error) => {
                console.warn("producer.close() failed:%o", error);
            });
        });
        producer.onReplacetrackDelegate.add((track, callback, errback) => {
            this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.replaceTrack(producer.localId, track);
            }), "producer @replacetrack event")
                .then(callback)
                .catch(errback);
        });
        producer.onSetmaxspatiallayerDelegate.add((spatialLayer, callback, errback) => {
            this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.setMaxSpatialLayer(producer.localId, spatialLayer);
            }), "producer @setmaxspatiallayer event")
                .then(callback)
                .catch(errback);
        });
        producer.onSetrtpencodingparametersDelegate.add((params, callback, errback) => {
            this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.setRtpEncodingParameters(producer.localId, params);
            }), "producer @setrtpencodingparameters event")
                .then(callback)
                .catch(errback);
        });
        producer.onNegotiationNeededDelegate.add(() => {
            this.createOffer();
        });
    }
    handleConsumer(consumer) {
        consumer.onCloseDelegate.add(() => {
            this.consumers.delete(consumer.id);
            this._pendingPauseConsumers.delete(consumer.id);
            this._pendingResumeConsumers.delete(consumer.id);
            if (this._closed)
                return;
            this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                yield this.handler.stopReceiving(consumer.localId);
            }), "consumer @close event")
                .catch(() => { });
        });
        consumer.onPauseDelegate.add(() => {
            // If Consumer is pending to be resumed, remove from pending resume list.
            if (this._pendingResumeConsumers.has(consumer.id)) {
                this._pendingResumeConsumers.delete(consumer.id);
            }
            // Store the Consumer into the pending list.
            this._pendingPauseConsumers.set(consumer.id, consumer);
            // There is no Consumer pause in progress, do it now.
            if (!this._consumerPauseInProgress) {
                this.pausePendingConsumers();
            }
        });
        consumer.onResumeDelegate.add(() => {
            // If Consumer is pending to be paused, remove from pending pause list.
            if (this._pendingPauseConsumers.has(consumer.id)) {
                this._pendingPauseConsumers.delete(consumer.id);
            }
            // Store the Consumer into the pending list.
            this._pendingResumeConsumers.set(consumer.id, consumer);
            // There is no Consumer resume in progress, do it now.
            if (!this._consumerResumeInProgress) {
                this.resumePendingConsumers();
            }
        });
        consumer.onNegotiationNeededDelegate.add(() => {
            serverConnection.send({
                channel: "rtc:negotiate",
                payload: {
                    id: this.id,
                    target: this.remoteClientId,
                    producerId: consumer.producerId
                }
            });
        });
    }
    createOffer(mediaId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("create offer", this._id, this.handler.peerConnection);
            yield this.awaitQueue
                .push(() => __awaiter(this, void 0, void 0, function* () {
                const desc = yield this.handler.createOffer();
                serverConnection.send({
                    channel: "rtc:offer",
                    payload: {
                        id: this.id,
                        sdp: desc.sdp,
                        target: this.remoteClientId,
                        mediaId
                    }
                });
                yield this.waitForAnswer().catch((error) => __awaiter(this, void 0, void 0, function* () {
                    console.error(error);
                    yield this.handler.rollbackLocalDescription();
                }));
                console.log("create offer finished");
            }), "createOffer")
                .catch((error) => {
                if (isExpectedTransportShutdownError(error)) {
                    return;
                }
                console.error(error);
            });
        });
    }
    waitForAnswer() {
        return __awaiter(this, void 0, void 0, function* () {
            this.clearWaitingForAnswer();
            yield new Promise((resolve, reject) => {
                const func = (sdp) => {
                    if (!sdp) {
                        reject(new Error("Peer can't answer"));
                        return;
                    }
                    this.clearWaitingForAnswer();
                    resolve();
                };
                // wait for answer
                const timeoutHandler = self.setTimeout(() => {
                    this.clearWaitingForAnswer();
                    reject(new Error("waiting for answer timeout"));
                }, WAITING_FOR_ANSWER_TIMEOUT);
                this.waitForAnswerData = {
                    timeoutHandler,
                    func
                };
                this.onAnswerDelegate.add(func);
            });
        });
    }
    clearWaitingForAnswer() {
        if (!this.waitForAnswerData)
            return;
        clearTimeout(this.waitForAnswerData.timeoutHandler);
        this.onAnswerDelegate.remove(this.waitForAnswerData.func);
        this.waitForAnswerData = undefined;
    }
}
const isExpectedTransportShutdownError = (error) => {
    if (!(error instanceof Error))
        return false;
    const message = error.message.toLowerCase();
    return ((message.includes("awaitqueue") && message.includes("closed")) ||
        (message.includes("transport") && message.includes("closed")) ||
        message.includes("invalidstateerror"));
};
